import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Box from "../components/Box/Box";
import { formDataAtom } from "../App";
import axios from "axios";
import Progressbar from "../components/Progressbar/Progressbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useRef, useState } from "react";
import Checkbox from "../components/Form/Checkbox";
import BackButton from "../components/Buttons/BackButton";
import ErrorList from "../components/Form/ErrorList";
import { handleSubmitClick } from "../components/Form/FormUtils";
import Spinner from "../components/Spinner/Spinner";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { WITH_APP, WITH_TRAVELCARD, WITH_BLIP, WITH_TICKET, NO_TICKET } from "./TicketType";
import { ADULT, NOT_ADULT } from "./AgeCheck";
import AppealForWho, { APPEAL_FOR_MYSELF, FIDUCIARY, HAVE_FIDUCIARY, PARENT } from "./AppealForWho";
import { FormModel } from "../types/FormTypes";

interface SummaryModel {
    gdprAccept: boolean;
}
interface Issue {
    id: string;
}

export const issueAtom = atom<Issue>({
    id: "",
});

export default function Summary() {
    const [formData, setFormData] = useAtom(formDataAtom);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
    const navigate = useNavigate();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setIssueId] = useAtom(issueAtom);
    const formRef = useRef<HTMLFormElement>(null);

    function GetEditButtonForNavigation(path: string, text?: string) {
        return (
            <button
                type="button"
                className="btn stripped icon-link p-0 justify-self-end change-link"
                onClick={() => navigate(path)}
            >
                <FontAwesomeIcon
                    className="text-xl mr-2 relative top-0.5 text-r"
                    icon={solid("edit")}
                />
                {text ?? "Gå tillbaka och ändra"}
            </button>
        );
    }

    return (
        <div>
            <Formik<SummaryModel>
                onSubmit={(values) => {
                    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
                    console.log("payload:", formData);

                    setIsFormSubmitted(true);
                    axios
                        .post(
                            `${apiBaseUrl?.replace(/\/$/, '') ?? ''}/crm/appealOfSurcharge `,
                            formData,
                            {
                                headers: {
                                    "referrer-policy": "strict-origin",
                                },
                            }
                        )
                        .then((result) => {
                            console.log("Actually sent payload:", formData);

                            setIssueId({ id: result.data });
                            setFormData({
                                surchargenumber: "",
                                ticketType: "",
                                travelcardnumber: "",
                                ticketNumber: "",
                                description: "",
                                firstname: "",
                                lastname: "",
                                phone: "",
                                email: "",
                                agecheck: "",
                                appealforwho: "",
                                reporterEmail: "",
                            });

                            const emailToShow = formData.appealforwho === APPEAL_FOR_MYSELF
                                ? formData.email
                                : formData.reporterEmail;

                            navigate("/tack", { state: { email: emailToShow } });
                        })
                        .catch((error) => {
                            navigate("../ett-fel-intraffade");
                        });
                }}
                initialValues={{
                    gdprAccept: false,
                }}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={Yup.object().shape({
                    gdprAccept: Yup.bool().test(
                        "checkbox-selected",
                        "Du måste godkänna hanteringen av dina personuppgifter.",
                        (value) => {
                            return value === true;
                        }
                    ),
                    ...formValidation(formData),
                })}
                enableReinitialize
            >
                {({ errors, values }) => (
                    <>
                        <div className="flex justify-between items-center">
                            <div className="flex justify-center h-15 md:h-auto mb-1">
                                <div
                                    className={`w-full text-left md:hidden ${isFormSubmitted === true ? "hide" : ""
                                        }`}
                                >
                                </div>
                            </div>

                            <div className="xs:w-52 md:w-full max-w-xs md:mb-4">
                                <Progressbar percentage={"100"} />
                            </div>
                        </div>

                        {console.log("Formik values on mount:", formData)}

                        <Form>

                            <Box fullwidth>
                                {isFormSubmitted && <Spinner label="Skickar ..." />}
                                <div
                                    className={`inner-summary ${isFormSubmitted === true ? "hide" : ""
                                        }`}
                                >
                                    <h2 ref={h2Ref} tabIndex={-1}>
                                        Granska din överklagan
                                    </h2>

                                    {Object.keys(errors).length > 0 && <ErrorList errors={errors} />}

                                    <p>
                                        Gå igenom alla uppgifter innan du skickar in din överklagan för tilläggsavgift.
                                    </p>
                                    <p>
                                        Om du vill göra ändringar, klicka på ”Gå tillbaka och ändra” Du kommer då till den del av formuläret där du kan justera dina uppgifter.
                                    </p>
                                    <p>
                                        När du är klar klickar du på ”Skicka överklagan”.
                                    </p>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Vad är det för nummer på tilläggsavgiften som ska överklagas?</h3>

                                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col gap-2">
                                                <span className="summary-data last">
                                                    {formData.surchargenumber}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="summary-edit">
                                                    {GetEditButtonForNavigation("../tillaggsavgiftsnummer")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Var fanns biljetten?</h3>

                                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col gap-2">
                                                <span className="summary-data last">
                                                    {getTicketsDetailsReadable(formData.ticketType)}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="summary-edit">
                                                    {GetEditButtonForNavigation("../biljettdetaljer")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {formData.ticketType === WITH_APP ? (
                                        <div className="summary-section patient-summary">
                                            <h3 className="summary-section-heading">Appbiljett</h3>

                                            <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                                <div>
                                                    <div>
                                                        <span className="summary-data-label">Mobilnummer:</span>
                                                        <span className="summary-data last">{formData.ticketNumber}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="summary-edit">
                                                        {GetEditButtonForNavigation("../biljett-med-app")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    ) : formData.ticketType === WITH_TRAVELCARD ? (
                                        <div className="summary-section patient-summary">
                                            <h3 className="summary-section-heading">Biljett via resekort</h3>

                                            <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                                <div>
                                                    <div>
                                                        <span className="summary-data-label">Resekortsnummer:</span>
                                                        <span className="summary-data last">{formData.ticketNumber}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="summary-edit">
                                                        {GetEditButtonForNavigation("../biljett-med-resekort")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : formData.ticketType === WITH_TICKET ? (
                                        <div className="summary-section patient-summary">
                                            <h3 className="summary-section-heading">Biljett via Pappersbiljett</h3>

                                            <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                                <div>
                                                    <div>
                                                        <span className="summary-data-label">Biljett-ID:</span>
                                                        <span className="summary-data last">{formData.ticketNumber}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="summary-edit">
                                                        {GetEditButtonForNavigation("../pappersbiljett")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : formData.ticketType === WITH_BLIP ? (
                                        <div className="summary-section patient-summary">
                                            <h3 className="summary-section-heading">Biljett via bankkort</h3>

                                            <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                                <div>
                                                    <div>
                                                        <span className="summary-data-label">Biljett-ID:</span>
                                                        <span className="summary-data last">{formData.ticketNumber}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="summary-edit">
                                                        {GetEditButtonForNavigation("../biljett-med-blipp")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Vad var det som hände?</h3>

                                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col gap-2">
                                                <span className="summary-data last">
                                                    {formData.description}

                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="summary-edit">
                                                    {GetEditButtonForNavigation("../vad-var-det-som-hande")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Är du över 18 år?</h3>

                                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col gap-2">
                                                <span className="summary-data last">
                                                    {getAgeCheckReadable(formData.agecheck)}

                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="summary-edit">
                                                    {GetEditButtonForNavigation("../ar-du-over-18")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Vem överklagar du för?</h3>

                                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col gap-2">
                                                <span className="summary-data last">
                                                    {getAppealForWhoReadable(formData.appealforwho)}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="summary-edit">
                                                    {GetEditButtonForNavigation("../vem-overklagar-du-for")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Kontaktuppgifter</h3>
                                        <h4>Resenär</h4>

                                        <div className="summary-data-wrapper ">
                                            <div className="mt-2 flex flex-col md:flex-row justify-between">


                                                <div className="flex flex-col gap-2">

                                                    <div>

                                                        <div>
                                                            {formData.agecheck === ADULT && formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                                                <>
                                                                    <span className="summary-data-label">Förnamn:</span>
                                                                    <span className="summary-data">{formData.firstname}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="summary-data-label">Resenärens förnamn:</span>
                                                                    <span className="summary-data">{formData.firstname}</span>
                                                                </>

                                                            )}
                                                        </div>

                                                    </div>

                                                    <div>

                                                        <div>

                                                            {formData.agecheck === ADULT && formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                                                <>
                                                                    <span className="summary-data-label">Efternamn:</span>
                                                                    <span className="summary-data">{formData.lastname}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="summary-data-label">Resenärens efternamn:</span>
                                                                    <span className="summary-data">{formData.lastname}</span>
                                                                </>

                                                            )}
                                                        </div>

                                                    </div>

                                                    <div>

                                                        <div>
                                                            {formData.agecheck === ADULT && formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                                                <>
                                                                    <span className="summary-data-label">Telefonnummer:</span>
                                                                    <span className="summary-data">{formData.phone}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="summary-data-label">Resenärens telefonnummer:</span>
                                                                    <span className="summary-data">{formData.phone}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {formData.agecheck === ADULT && formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                                        <>
                                                            <div>
                                                                <span className="summary-data-label">Mejladress:</span>
                                                                <span className="summary-data">{formData.email}</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h4 className="mt-4">Ombud/god man/vårdnadshavare</h4>
                                                            <div>
                                                                <span className="summary-data-label">Mejladress för kontakt:</span>
                                                                <span className="summary-data">
                                                                    <span className="summary-data">{formData.reporterEmail}</span>
                                                                </span>
                                                            </div>
                                                        </>

                                                    )}

                                                </div>

                                                <div className="flex flex-col">
                                                    <div className="summary-edit">
                                                        {GetEditButtonForNavigation("../kontaktuppgifter")}

                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="mt-6 mb-6 md:mb-8">
                                        <Checkbox name="gdprAccept">
                                            Jag godkänner att Östgötatrafiken sparar och hanterar mina
                                            personuppgifter enligt{" "}
                                            <Link
                                                target="_blank"
                                                className="red-link underline hover:no-underline"
                                                to="https://www.ostgotatrafiken.se/info/integritetspolicy-for-ostgotatrafikens-kunder/"
                                            >
                                                relevant dataskyddslagstiftning.
                                                <FontAwesomeIcon
                                                    icon={solid("external-link")}
                                                    className="ml-2"
                                                />
                                            </Link>
                                        </Checkbox>
                                    </div>
                                </div>
                            </Box>

                            <div
                                className={`flex justify-centery button-container ${isFormSubmitted === true ? "hide" : ""
                                    }`}
                            >
                                <div className="w-full text-left flex items-center">
                                    <BackButton />
                                </div>
                                <div className="w-full text-right">
                                    <button
                                        onClick={handleSubmitClick(formRef)}
                                        className="btn primary"
                                        type="submit"
                                    >
                                        Skicka överklagan
                                    </button>
                                </div>
                            </div>
                        </Form>
                    </>
                )}
            </Formik>
        </div>
    );
}

function getTicketsDetailsReadable(ticketType: string): string {
    return ticketType === WITH_APP ? "I appen" :
        ticketType === WITH_TRAVELCARD ? "På ett resekort" :
            ticketType === WITH_BLIP ? "Blippat bankkort ombord" :
                ticketType === WITH_TICKET ? "Pappersbiljett" :
                    ticketType === NO_TICKET ? "Inget av ovanstående/det fanns ingen biljett" :
                        "";
}

function getAgeCheckReadable(agecheck: string): string {
    return agecheck === ADULT ? "Ja, jag är över 18 år" :
        agecheck === NOT_ADULT ? "Nej, jag är under 18 år" :
            "";
}


function getAppealForWhoReadable(appealforwho: string): string {
    return appealforwho === APPEAL_FOR_MYSELF ? "Jag överklagar för mig själv" :
        appealforwho === FIDUCIARY ? "Jag är god man/ombud/vårdnadshavare åt resenären" :
            appealforwho === PARENT ? "Jag är vårdnadshavare/förälder åt resenären" :
                appealforwho === HAVE_FIDUCIARY ? "Jag har en god man/ett ombud" :
                    "Jag är under 18 år";
}


function formValidation(data: FormModel): any {
    return {
        surchargenumber: Yup.string().test(
            "valid-surchargenumber",
            "Du måste fylla i ett giltigt tilläggsavgiftsnummer.",
            () => data.surchargenumber.trim().length > 0
        ),

        firstname: Yup.string().test(
            "firstname-required",
            "Förnamn måste fyllas i.",
            () => data.firstname.trim().length > 0
        ),

        lastname: Yup.string().test(
            "lastname-required",
            "Efternamn måste fyllas i.",
            () => data.lastname.trim().length > 0
        ),

        phone: Yup.string().test(
            "phone-required",
            "Telefonnummer måste fyllas i.",
            () => data.phone.trim().length > 0
        ),
    };
}
